import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Toast } from 'sharedUi/Toast';
import { RefreshButton } from 'sharedUi/RefreshButton';
import { Button } from 'primereact/button';
import { useDebounce } from '../../hooks/useDebounce';
import { useAppNavigate } from '../../hooks/useAppNavigate';
import { extractErrorMessage } from '../../utils/error-handler';
import { connectionTypesService } from '../../services/connection-types.service';
import type { ConnectionType } from '../../types/partners';
import DeleteConfirmDialog from '../../components/dialogs/delete-confirm-dialog';
import PermissionGuard from '../../components/guards/PermissionGuard';
import { useCanCreate, useCanUpdate, useCanDelete } from '../../hooks/usePermissions';
import { JOB_NAMES } from '../../constants/jobNames';
import PageHeader from '../../components/common/page-header';
import SaasDataTable, {
  type SaasColumn, buildDefaultFilters, buildColumnFiltersParam,
} from '../../components/common/saas-data-table';

const FILTER_DEFS: SaasColumn[] = [
  { field: 'code', header: 'Code', filter: true },
  { field: 'name', header: 'Name', filter: true },
];

const ConnectionTypesList = ({ embedded = false }: { embedded?: boolean }) => {
  const navigate = useAppNavigate();
  const toast = useRef<Toast>(null);
  const canCreate = useCanCreate(JOB_NAMES.CONNECTION_TYPES);
  const canUpdate = useCanUpdate(JOB_NAMES.CONNECTION_TYPES);
  const canDelete = useCanDelete(JOB_NAMES.CONNECTION_TYPES);

  const [rows, setRows] = useState<ConnectionType[]>([]);
  const [loading, setLoading] = useState(true);
  const includeDeleted = false; // "Show deleted" toggle removed; always hide deleted rows
  const [searchText, setSearchText] = useState('');
  const [first, setFirst] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<1 | -1 | null>(null);
  const [filters, setFilters] = useState<Record<string, any>>(() => buildDefaultFilters(FILTER_DEFS));

  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const debouncedSearch = useDebounce(searchText, 500);
  const notify = (severity: 'success' | 'error', detail: string) =>
    toast.current?.show({ severity, summary: severity === 'success' ? 'Success' : 'Error', detail, life: severity === 'error' ? 8000 : 3000 });

  const fetchRows = useCallback(async (page = 1, size = pageSize, sortBy = sortField, order = sortOrder, active = filters) => {
    try {
      setLoading(true);
      const params: any = { page, page_size: size };
      if (includeDeleted) params.include_deleted = true;
      if (debouncedSearch?.trim()) params.search = debouncedSearch.trim();
      if (sortBy) { params.sort_by = sortBy; params.sort_order = order === 1 ? 'asc' : 'desc'; }
      const fp = buildColumnFiltersParam(active);
      if (fp) params.filters = fp;
      const res = await connectionTypesService.getAll(params);
      setRows(res.data || []);
      setTotalRecords(res.pagination?.totalItems ?? (res.pagination as any)?.total ?? 0);
    } catch (err: any) {
      notify('error', extractErrorMessage(err, 'Failed to load connection types.'));
    } finally {
      setLoading(false);
    }
  }, [includeDeleted, debouncedSearch, pageSize, sortField, sortOrder, filters]);

  useEffect(() => { setFirst(0); fetchRows(1, pageSize); }, [includeDeleted, debouncedSearch]); // eslint-disable-line

  const onPage = (e: any) => { setFirst(e.first); setPageSize(e.rows); fetchRows(Math.floor(e.first / e.rows) + 1, e.rows, sortField, sortOrder); };
  const onSort = (e: any) => { setSortField(e.sortField); setSortOrder(e.sortOrder); setFirst(0); fetchRows(1, pageSize, e.sortField, e.sortOrder); };
  const onFilter = (e: any) => { const next = e.filters || {}; setFilters(next); setFirst(0); fetchRows(1, pageSize, sortField, sortOrder, next); };
  const refresh = () => fetchRows(Math.floor(first / pageSize) + 1, pageSize, sortField, sortOrder);

  const doDelete = async () => {
    if (!deleteId) return;
    try { setDeleteLoading(true); await connectionTypesService.delete(deleteId); setDeleteId(null); refresh(); }
    catch (err: any) { setDeleteId(null); notify('error', extractErrorMessage(err, 'Failed to delete connection type')); }
    finally { setDeleteLoading(false); }
  };
  const doRestore = async (id: string) => {
    try { await connectionTypesService.restore(id); notify('success', 'Restored'); refresh(); }
    catch (err: any) { notify('error', extractErrorMessage(err, 'Failed to restore')); }
  };

  const columns: SaasColumn<ConnectionType>[] = useMemo(() => {
    const cols: SaasColumn<ConnectionType>[] = [
      { field: 'code', header: 'Code', sortable: true, filter: true, hideable: false, body: (r) => <span className="font-mono text-xs">{r.code}</span> },
      { field: 'name', header: 'Name', sortable: true, filter: true },
      { field: 'supportedAuthTypes', header: 'Auth Types', body: (r) => (r.supportedAuthTypes || []).join(', ') || '-' },
      { field: 'isActive', header: 'Active', align: 'center', body: (r) => <i className={`pi ${r.isActive ? 'pi-check-circle text-emerald-600' : 'pi-times-circle text-gray-400'}`} /> },
    ];
    if (includeDeleted) cols.push({ field: 'status', header: 'Status', body: (r) => { const d = (r as any).isDelete; return <span className={d ? 'text-rose-600' : 'text-emerald-600'}>{d ? 'Deleted' : 'Active'}</span>; } });
    cols.push({
      field: 'actions', header: 'Actions', align: 'center', hideable: false,
      body: (r) => {
        const isDeleted = (r as any).isDelete;
        return (
          <div className="flex gap-1 justify-center">
            <button aria-label="View" className="p-2 rounded hover:bg-emerald-50 dark:hover:bg-emerald-900/20" onClick={() => navigate(`/partners/connection-types/${r._id}`)} data-pr-tooltip="View"><i className="pi pi-eye text-emerald-600" style={{ fontSize: '1.05rem' }} /></button>
            {!isDeleted && canUpdate && <button aria-label="Edit" className="p-2 rounded hover:bg-blue-50 dark:hover:bg-blue-900/20" onClick={() => navigate(`/partners/connection-types/${r._id}/edit`)} data-pr-tooltip="Edit"><i className="pi pi-pencil text-blue-600" style={{ fontSize: '1.05rem' }} /></button>}
            {!isDeleted && canDelete && <button aria-label="Delete" className="p-2 rounded hover:bg-red-50 dark:hover:bg-red-900/20" onClick={() => setDeleteId(r._id)} data-pr-tooltip="Delete"><i className="pi pi-trash text-red-600" style={{ fontSize: '1.05rem' }} /></button>}
            {isDeleted && canDelete && <button aria-label="Refresh" className="p-2 rounded hover:bg-emerald-50 dark:hover:bg-emerald-900/20" onClick={() => doRestore(r._id)} data-pr-tooltip="Restore"><i className="pi pi-refresh text-emerald-600" style={{ fontSize: '1.05rem' }} /></button>}
          </div>
        );
      },
    });
    return cols;
  }, [canUpdate, canDelete, includeDeleted]);

  return (
    <PermissionGuard jobName={JOB_NAMES.CONNECTION_TYPES}>
      <Toast ref={toast} position="top-right" />
      <div className={embedded ? '' : 'p-3'} data-testid="SCR-ConnectionTypes-List">
        {!embedded && (
          <PageHeader icon="usb" title="Connection Types" subtitle="Configurable catalogue of connection kinds" />
        )}
        <div className={embedded ? '' : 'bg-surface rounded-xl shadow-sm border border-line overflow-hidden'}>
          <SaasDataTable
            tableId="connection-types" columns={columns} data={rows} loading={loading} dataKey="_id"
            emptyMessage="No connection types found" first={first} rows={pageSize} totalRecords={totalRecords}
            onPage={onPage} rowsPerPageOptions={[5, 10, 25, 50]} sortField={sortField || undefined} sortOrder={sortOrder || undefined}
            onSort={onSort} filters={filters} onFilter={onFilter} globalSearch={searchText} onGlobalSearchChange={setSearchText}
            globalSearchPlaceholder="Search connection types..." testId="ConnectionTypesList"
            toolbarEnd={<><span className="text-sm text-ink-muted whitespace-nowrap">{totalRecords} {totalRecords === 1 ? 'record' : 'records'}</span><RefreshButton onClick={refresh} loading={loading} />{canCreate && <Button label="Create Type" icon="pi pi-plus" size="small" onClick={() => navigate('/partners/connection-types/create')} />}</>}
          />
        </div>

        <DeleteConfirmDialog visible={!!deleteId} onCancel={() => setDeleteId(null)} onDelete={doDelete} message="Delete this connection type? It will be marked as deleted." loading={deleteLoading} />
      </div>
    </PermissionGuard>
  );
};

export default ConnectionTypesList;
